import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CrystalIcon } from './CrystalIcon';
import { Crosshair, Lock } from 'lucide-react';
import { kindOf } from './OathHits';
import { PILLARS, DISTRACTIONS, lastNDays } from './oathTheme';

const P = PILLARS[2];
const FOCUS_IDEAS = ['My top goal today', 'My health', 'My family', 'Growing my business', 'My inner peace'];

const ObsessionIndex = ({ index, lockDays }) => {
  const locked = new Set(lockDays);
  return (
    <div className="rounded-2xl px-4 py-4" data-testid="oath-obsession-card"
      style={{ background: 'linear-gradient(135deg, rgba(93,212,255,0.12), rgba(185,120,255,0.10))', border: `1px solid ${P.accent}40` }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-['Outfit'] text-[10px] tracking-[0.25em] uppercase text-white/45">Obsession Index · days locked in a row</p>
          <p className="font-['Outfit'] text-4xl font-light leading-none mt-1" style={{ color: P.light }} data-testid="oath-obsession-value">
            {index}<span className="text-sm text-white/50 ml-2">day{index === 1 ? '' : 's'}</span>
          </p>
        </div>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}>
          <Crosshair size={30} style={{ color: P.accent, filter: `drop-shadow(0 0 8px ${P.accent})` }} />
        </motion.div>
      </div>
      <div className="flex gap-1.5 mt-4 items-end h-10">
        {lastNDays(7).map((d, i) => (
          <motion.div key={d} initial={{ height: 4 }} animate={{ height: locked.has(d) ? 40 : 6 }} transition={{ delay: i * 0.05 }} className="flex-1 rounded-t-md" title={d}
            style={{ background: locked.has(d) ? `linear-gradient(180deg, ${P.light}, #B978FF)` : 'rgba(255,255,255,0.07)', boxShadow: locked.has(d) ? `0 0 10px ${P.glow}` : 'none' }} />
        ))}
      </div>
    </div>
  );
};

const WeekOfLight = ({ hits }) => (
  <div>
    <p className="font-['Cinzel'] text-sm tracking-[0.15em]" style={{ color: P.light }}>Blindspot Filter · Week of Light</p>
    <p className="font-['Outfit'] text-xs text-white/50 mt-1">Your weekly review: the good things you logged in Grateful Fix, shown on purpose with the noise blurred out — so your eyes learn to see only the good.</p>
    <div className="relative rounded-3xl p-4 mt-3 overflow-hidden min-h-[120px]" style={{ background: '#07070F', border: `1px solid ${P.accent}30` }} data-testid="oath-blindspot">
      {DISTRACTIONS.slice(0, 10).map((w, i) => (
        <span key={w} className="absolute font-['Outfit'] text-white/25 select-none pointer-events-none" style={{ left: `${(i * 37) % 85}%`, top: `${(i * 23) % 85}%`, filter: 'blur(3px)', fontSize: 12 + (i % 3) * 4 }}>{w}</span>
      ))}
      <div className="relative space-y-2">
        {hits.length === 0 ? <p className="font-['Outfit'] text-xs text-white/45 text-center py-6">Log gratitude in Pillar II and it will shine here.</p>
          : hits.slice(0, 10).map((h, i) => {
            const k = kindOf(h.kind);
            return (
              <motion.div key={h.id} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.04 }}
                className="flex items-center gap-3 rounded-xl px-3 py-2 backdrop-blur-md" style={{ background: 'rgba(5,5,10,0.75)', border: `1px solid ${k.color}40` }}>
                <CrystalIcon kind={h.kind} size={22} /><p className="flex-1 font-['Outfit'] text-[13px] text-white/90">{h.text}</p>
                <span className="font-['Outfit'] text-[9px] text-white/30">{h.date.slice(5)}</span>
              </motion.div>
            );
          })}
      </div>
    </div>
  </div>
);

export const OathLock = ({ state, onStartGame, busy }) => {
  const [focus, setFocus] = useState('');
  return (
    <div className="space-y-5">
      <div>
        <p className="font-['Cinzel'] text-sm tracking-[0.15em]" style={{ color: P.light }}>Why Laser Lock?</p>
        <p className="font-['Outfit'] text-xs text-white/50 mt-1">Whatever you focus on grows. Step 1: pick ONE good thing to fixate on today. Step 2: play the 45-second Lock-in game and tap every distraction to shatter it. Survive and your focus is locked (+15 power).</p>
      </div>
      {state.locked_today ? (
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="rounded-3xl p-5 text-center" data-testid="oath-locked-badge"
          style={{ background: `${P.accent}12`, border: `1px solid ${P.accent}80`, boxShadow: `0 0 22px ${P.glow}` }}>
          <Lock size={20} className="mx-auto" style={{ color: P.light }} />
          <p className="font-['Outfit'] text-[10px] tracking-[0.3em] uppercase mt-2" style={{ color: P.accent }}>Focus locked today</p>
          <p className="font-['Cormorant_Garamond'] italic text-2xl text-white mt-1" data-testid="oath-today-focus">{state.today_focus}</p>
        </motion.div>
      ) : (
        <div className="space-y-3">
          <p className="font-['Outfit'] text-[10px] tracking-[0.25em] uppercase text-white/40">Today I lock my mind on…</p>
          <div className="flex flex-wrap gap-2">
            {FOCUS_IDEAS.map((f, i) => (
              <button key={f} type="button" onClick={() => setFocus(f)} data-testid={`oath-focus-idea-${i}`} className="px-3 py-1.5 rounded-full font-['Outfit'] text-[11px]"
                style={{ border: `1px solid ${focus === f ? P.accent : 'rgba(93,212,255,0.25)'}`, background: focus === f ? `${P.accent}22` : 'transparent', color: focus === f ? P.light : 'rgba(255,255,255,0.6)' }}>{f}</button>
            ))}
          </div>
          <input value={focus} onChange={(e) => setFocus(e.target.value.slice(0, 120))} placeholder="Or type your one focus…" data-testid="oath-focus-input"
            className="w-full rounded-full px-4 py-3 bg-white/[0.04] font-['Outfit'] text-sm text-white placeholder:text-white/25 outline-none" style={{ border: `1px solid ${P.accent}50` }} />
          <motion.button type="button" whileTap={{ scale: 0.95 }} disabled={busy || focus.trim().length < 2} onClick={() => onStartGame(focus.trim())} data-testid="oath-lock-btn"
            className="w-full py-4 rounded-full font-['Cinzel'] tracking-[0.25em] text-sm text-[#021018] disabled:opacity-35"
            style={{ background: 'linear-gradient(135deg, #D4F3FF, #5DD4FF 55%, #B978FF)', boxShadow: `0 0 26px ${P.glow}` }}>
            START LOCK-IN · 45s
          </motion.button>
        </div>
      )}
      <ObsessionIndex index={state.obsession_index} lockDays={state.lock_days_week} />
      <WeekOfLight hits={state.hits_week} />
    </div>
  );
};

export default OathLock;
